/*
 *  -----------------------------------------------------------------------
 *    <copyright file="custom-report-helpers.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import {
    ReportDefinitionFields,
    ReportDefinitionAuditFields_ReportDefinition_tabs_ReportDefinitionTab as ReportDefinitionFields_tabs,
    ReportDefinitionAuditFields_ReportDefinition_tabs_ReportDefinitionTab_columns_ReportDefinitionColumn as ReportDefinitionFields_tabs_columns,
} from 'queries/generated/fragments.generated';
import { ReportDefinitionFieldTypes, ReportDefinitionTabs } from 'types/graphql-global-types';
import { notEmpty } from '@utils/utility-helpers';

export interface AddEditCustomReportState {
    reportDefinition: ReportDefinitionFields;
    optionsTabs: ReportDefinitionFields_tabs[];
}

export interface AddEditCustomReportErrors {
    [key: string]: string | undefined | null;
    name?: string;
    description?: string;
    tabs?: string;
}

export enum CustomReportPage {
    step1Name = 1,
    step2Data = 2,
    step3Columns = 3,
}

export const getTab = (
    tabs: (ReportDefinitionFields_tabs | null)[] | null,
    tabType: ReportDefinitionTabs | null | undefined,
) => {
    if (!tabs || !tabType) return undefined;
    return tabs.filter(notEmpty).find((tab) => tab.tabType === tabType);
};

export const containsTab = (
    tabs: (ReportDefinitionFields_tabs | null)[] | null,
    tabType: ReportDefinitionTabs | null,
) => tabs?.some((tab) => tab?.tabType === tabType);

export const containsColumn = (
    columns: (ReportDefinitionFields_tabs_columns | null)[] | null,
    dataField: ReportDefinitionFieldTypes | null | undefined,
) => {
    if (!columns || !dataField) return false;
    return columns.filter(notEmpty).some((column) => column.dataField === dataField);
};

export const copyTab = (tab?: ReportDefinitionFields_tabs | null) =>
    ({
        __typename: 'ReportDefinitionTab',
        tabType: tab?.tabType,
        columns: [...(tab?.columns ?? [])],
    }) as ReportDefinitionFields_tabs;

export const copyTabFromTabArray = (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => {
    const tab = getTab(tabs, tabType);

    return copyTab(tab);
};

export const copyTabs = (tabs: (ReportDefinitionFields_tabs | null)[] | null) => tabs?.map((tab) => copyTab(tab));

export const addColumnToTab = (tab: ReportDefinitionFields_tabs, dataField: ReportDefinitionFieldTypes) => {
    if (containsColumn(tab.columns, dataField)) return tab;

    const newTab = copyTab(tab);

    const column = {
        __typename: 'ReportDefinitionColumn',
        dataField,
        index: 0,
    } as ReportDefinitionFields_tabs_columns;

    newTab.columns?.unshift(column);

    return newTab;
};

export const removeColumnFromTab = (tab: ReportDefinitionFields_tabs, dataField: ReportDefinitionFieldTypes) => {
    const columns = tab.columns?.filter((column) => column?.dataField !== dataField);

    return {
        ...tab,
        columns: [...(columns ?? [])],
    } as ReportDefinitionFields_tabs;
};

export const replaceTab = (
    tabs: ReportDefinitionFields_tabs[],
    oldTab: ReportDefinitionFields_tabs,
    newTab: ReportDefinitionFields_tabs,
) => {
    const index = tabs.indexOf(oldTab);

    tabs.splice(index, 1, newTab);
};

export const updateTabByTabType = (
    tabs: ReportDefinitionFields_tabs[],
    tabType: ReportDefinitionTabs,
    updateFunction: (tab: ReportDefinitionFields_tabs) => ReportDefinitionFields_tabs,
) => {
    const oldTab = getTab(tabs, tabType);

    if (!oldTab) return;

    const newTab = updateFunction(oldTab);

    replaceTab(tabs, oldTab, newTab);
};

export const getNotMatchingColumns = (
    sourceTab: ReportDefinitionFields_tabs | null | undefined,
    comparisonTab: ReportDefinitionFields_tabs | null | undefined,
): ReportDefinitionFields_tabs_columns[] => {
    if (!sourceTab?.columns || !comparisonTab?.columns) return [] as ReportDefinitionFields_tabs_columns[];

    return sourceTab.columns?.filter(
        (column) => !containsColumn(comparisonTab.columns, column?.dataField),
    ) as ReportDefinitionFields_tabs_columns[];
};

export const removeMatchingColumns = (
    targetTabs: ReportDefinitionFields_tabs[],
    comparisonTabs: (ReportDefinitionFields_tabs | null)[] | null,
) => {
    comparisonTabs?.filter(notEmpty).forEach((tab) => {
        const optionsTab = getTab(targetTabs, tab.tabType);
        if (optionsTab) {
            optionsTab.columns = getNotMatchingColumns(optionsTab, tab);
        }
    });
};

export const getNotMatchingTabs = (
    sourceTabs: ReportDefinitionFields_tabs[],
    comparisonTabs: ReportDefinitionFields_tabs[],
): ReportDefinitionFields_tabs[] =>
    sourceTabs.filter((tab: ReportDefinitionFields_tabs) => !containsTab(comparisonTabs, tab.tabType));

export const clearColumnsByTabType = (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => {
    const tab = getTab(tabs, tabType);
    if (!tab) return;
    tab.columns = [];
};

export const copyReportDefinition = (reportDefinition: ReportDefinitionFields) =>
    ({
        __typename: 'ReportDefinition',
        id: reportDefinition.id,
        userId: reportDefinition.userId,
        deleted: reportDefinition.deleted,
        name: reportDefinition.name,
        description: reportDefinition.description,
        includeInternalRisks: reportDefinition.includeInternalRisks,
        includeNotApplicableRisks: reportDefinition.includeNotApplicableRisks,
        includeDraftRisks: reportDefinition.includeDraftRisks,
        includeObsoleteRisks: reportDefinition.includeObsoleteRisks,
        recordId: reportDefinition.recordId,
        tabs: copyTabs(reportDefinition.tabs),
        created: reportDefinition.created,
        createdBy: reportDefinition.createdBy,
        updated: reportDefinition.updated,
        updatedBy: reportDefinition.updatedBy,
    }) as ReportDefinitionFields;
